import { formatCount, formatDuration } from '@/lib/format';
import type { GridMode } from '@/cache/types';

/**
 * The signature element.
 *
 * Every grid carries a strip stating where its interactions are computed and
 * how much of the 100 ms "instant" budget the last one used. The local cache
 * (spec §4.7) only pays off if the user can trust it, and a cache that silently
 * degrades to server round-trips is worse than no cache -- so the mode is never
 * hidden, and the fallback always says why in plain words.
 */

const PERCEPTUAL_BUDGET_MS = 100;

const LABEL: Record<GridMode, string> = {
  idle: 'No result',
  materializing: 'Caching',
  local: 'Local',
  remote: 'Server',
  unavailable: 'Server',
};

const DESCRIPTION: Record<GridMode, string> = {
  idle: 'Run a cell to load a result.',
  materializing: 'Loading rows into the in-browser DuckDB cache.',
  local: 'Sort, filter and column stats run in this tab. No network.',
  remote: 'Sort and filter run on the server.',
  unavailable: 'Sort and filter run on the server.',
};

export interface ModeStripProps {
  readonly mode: GridMode;
  readonly reason: string | null;
  readonly cachedRows: number;
  readonly totalRows: number;
  readonly lastViewMs: number | null;
}

export function ModeStrip({
  mode,
  reason,
  cachedRows,
  totalRows,
  lastViewMs,
}: ModeStripProps): JSX.Element {
  const busy = mode === 'materializing';
  const elapsed = lastViewMs ?? 0;
  const overBudget = elapsed > PERCEPTUAL_BUDGET_MS;
  const fill = Math.min(100, (elapsed / PERCEPTUAL_BUDGET_MS) * 100);

  return (
    <div
      className="mode-strip"
      data-mode={mode}
      data-busy={busy ? 'true' : 'false'}
      data-testid="mode-strip"
      role="status"
      aria-live="polite"
      aria-label={`Execution mode: ${LABEL[mode]}. ${reason ?? DESCRIPTION[mode]}`}
    >
      <span className="mode-tag">
        <span className="mode-dot" aria-hidden="true" />
        {LABEL[mode]}
      </span>

      <span className="text-[10px] text-muted">{reason ?? DESCRIPTION[mode]}</span>

      <div className="ml-auto flex items-center gap-2.5">
        {mode === 'local' && cachedRows > 0 ? (
          <span className="tnum text-[10px] text-faint" title={`${cachedRows} rows cached in DuckDB-WASM`}>
            {formatCount(cachedRows)} cached
          </span>
        ) : null}

        {totalRows > 0 ? (
          <span className="tnum text-[10px] text-faint">{formatCount(totalRows)} rows</span>
        ) : null}

        {lastViewMs !== null ? (
          <>
            <span
              className="tnum text-[10px]"
              style={{ color: overBudget ? 'var(--c-remote)' : 'var(--c-muted)' }}
            >
              {formatDuration(elapsed)}
            </span>
            <div
              className="budget-track"
              data-over={overBudget ? 'true' : 'false'}
              title={`${Math.round(elapsed)} ms of the ${PERCEPTUAL_BUDGET_MS} ms perceptual budget`}
              role="img"
              aria-label={`Last interaction used ${Math.round(elapsed)} of ${PERCEPTUAL_BUDGET_MS} milliseconds`}
            >
              <div className="budget-fill" style={{ width: `${fill}%` }} />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
